import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ChevronsUpDown, Search, Plus, Check } from 'lucide-react';
import { cn, Avatar, Badge, Spinner } from '@mellify/ui';
import { useWorkspaces } from '../../hooks/useWorkspaces';
import { useWorkspaceSwitcher } from '../../hooks/useWorkspaceSwitcher';
import { useWorkspaceStore } from '../../stores/workspaceStore';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../routes/routeConfig';
import type { WorkspaceListItem } from '../../services/workspaceService';
import type { SubscriptionPlan } from '@mellify/shared';

export interface WorkspaceSwitcherProps {
  /** Whether the sidebar is collapsed */
  isCollapsed?: boolean;
}

const planLabels: Record<SubscriptionPlan, string> = {
  free: 'Free Plan',
  starter: 'Starter',
  professional: 'Professional',
  enterprise: 'Enterprise',
};

const planBadgeVariants: Record<SubscriptionPlan, 'dark' | 'primary' | 'info' | 'warning'> = {
  free: 'dark',
  starter: 'primary',
  professional: 'info',
  enterprise: 'warning',
};

export const WorkspaceSwitcher: React.FC<WorkspaceSwitcherProps> = ({
  isCollapsed = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace);
  const { data: workspaces, isLoading } = useWorkspaces();
  const { switchWorkspace } = useWorkspaceSwitcher();

  // Filter workspaces based on search
  const filteredWorkspaces = useMemo(() => {
    if (!workspaces) return [];
    if (!searchQuery.trim()) return workspaces;

    const query = searchQuery.toLowerCase().trim();
    return workspaces.filter(
      (ws) =>
        ws.name.toLowerCase().includes(query) ||
        ws.slug.toLowerCase().includes(query)
    );
  }, [workspaces, searchQuery]);

  // Calculate dropdown position
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + 4,
      left: rect.left,
      width: isCollapsed ? 260 : rect.width,
    });
  }, [isCollapsed]);

  // Toggle dropdown
  const toggleDropdown = useCallback(() => {
    setIsOpen((prev) => {
      if (!prev) {
        setSearchQuery('');
        requestAnimationFrame(() => {
          updatePosition();
          // Focus search after open
          requestAnimationFrame(() => {
            searchInputRef.current?.focus();
          });
        });
      }
      return !prev;
    });
  }, [updatePosition]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        closeDropdown();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeDropdown();
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, closeDropdown]);

  // Handle workspace selection
  const handleSelectWorkspace = useCallback(
    async (workspace: WorkspaceListItem) => {
      closeDropdown();
      if (workspace.id === currentWorkspace?.id) return;
      await switchWorkspace(workspace);
    },
    [switchWorkspace, currentWorkspace, closeDropdown]
  );

  // Handle create new workspace
  const handleCreateNew = useCallback(() => {
    closeDropdown();
    navigate(ROUTES.DASHBOARD_WORKSPACES);
  }, [closeDropdown, navigate]);

  const displayName = currentWorkspace?.name || 'Select Workspace';
  const displayPlan = currentWorkspace
    ? planLabels[currentWorkspace.subscriptionPlan]
    : 'No workspace';

  return (
    <>
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={toggleDropdown}
        className={cn(
          'group flex items-center gap-3 w-full rounded-lg border border-dark-200 dark:border-dark-700 bg-white dark:bg-dark-800 hover:bg-dark-50 dark:hover:bg-dark-700 transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
          isCollapsed ? 'justify-center p-2' : 'px-3 py-2.5'
        )}
        title={isCollapsed ? displayName : undefined}
        aria-label="Switch workspace"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <Avatar
          src={currentWorkspace?.logoUrl}
          name={currentWorkspace?.name}
          size="sm"
          className="shrink-0"
        />
        {!isCollapsed && (
          <>
            <div className="flex-1 text-left min-w-0">
              <p className="text-sm font-semibold text-dark-900 dark:text-dark-100 truncate">
                {displayName}
              </p>
              <p className="text-xs text-dark-500 dark:text-dark-400 truncate">
                {displayPlan}
              </p>
            </div>
            <ChevronsUpDown className="w-4 h-4 shrink-0 text-dark-400 dark:text-dark-500 group-hover:text-dark-600 dark:group-hover:text-dark-300 transition-colors" />
          </>
        )}
      </button>

      {/* Dropdown Portal */}
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-dropdown bg-white dark:bg-dark-800 rounded-lg border border-dark-200 dark:border-dark-700 shadow-dropdown overflow-hidden animate-scale-in origin-top-left"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              minWidth: 240,
            }}
            role="listbox"
            aria-label="Workspace list"
          >
            {/* Search */}
            <div className="px-3 py-2 border-b border-dark-100 dark:border-dark-700">
              <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-dark-50 dark:bg-dark-700/50 border border-dark-200 dark:border-dark-600">
                <Search className="w-3.5 h-3.5 text-dark-400 dark:text-dark-500 shrink-0" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search workspaces..."
                  className="flex-1 bg-transparent text-xs text-dark-900 dark:text-dark-100 placeholder:text-dark-400 dark:placeholder:text-dark-500 outline-none"
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
            </div>

            {/* Workspace List */}
            <div className="max-h-[240px] overflow-y-auto scrollbar-thin py-1">
              {isLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Spinner size="sm" variant="primary" />
                </div>
              ) : filteredWorkspaces.length === 0 ? (
                <div className="py-4 text-center">
                  <p className="text-xs text-dark-500 dark:text-dark-400">
                    {searchQuery ? 'No workspaces found' : 'No workspaces yet'}
                  </p>
                </div>
              ) : (
                filteredWorkspaces.map((workspace) => {
                  const isActive = currentWorkspace?.id === workspace.id;

                  return (
                    <button
                      key={workspace.id}
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      onClick={() => handleSelectWorkspace(workspace)}
                      className={cn(
                        'flex items-center gap-2.5 w-full px-3 py-2 text-left transition-colors outline-none',
                        isActive
                          ? 'bg-primary-50 dark:bg-primary-900/20'
                          : 'hover:bg-dark-50 dark:hover:bg-dark-700/50'
                      )}
                    >
                      <Avatar
                        src={workspace.logoUrl}
                        name={workspace.name}
                        size="xs"
                        className="shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-dark-900 dark:text-dark-100 truncate">
                          {workspace.name}
                        </p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <Badge
                            variant={planBadgeVariants[workspace.subscriptionPlan]}
                            size="sm"
                          >
                            {planLabels[workspace.subscriptionPlan]}
                          </Badge>
                          <span className="text-[10px] text-dark-400 dark:text-dark-500">
                            {workspace.memberCount} {workspace.memberCount === 1 ? 'member' : 'members'}
                          </span>
                        </div>
                      </div>
                      {isActive && (
                        <Check className="w-4 h-4 text-primary-500 shrink-0" />
                      )}
                    </button>
                  );
                })
              )}
            </div>

            {/* Create New Workspace */}
            <div className="border-t border-dark-100 dark:border-dark-700 py-1">
              <button
                type="button"
                onClick={handleCreateNew}
                className="flex items-center gap-2.5 w-full px-3 py-2 text-left hover:bg-dark-50 dark:hover:bg-dark-700/50 transition-colors outline-none"
              >
                <div className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-dark-100 dark:bg-dark-700 shrink-0">
                  <Plus className="w-3.5 h-3.5 text-dark-500 dark:text-dark-400" />
                </div>
                <span className="text-sm font-medium text-dark-700 dark:text-dark-300">
                  Create New Workspace
                </span>
              </button>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

WorkspaceSwitcher.displayName = 'WorkspaceSwitcher';